import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';           // ← added
import {
  Box,
  Typography,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormControl,
  Card,
  CardContent,
  Button,
  CircularProgress,
  Alert,
} from '@mui/material';

import config from "../../twh/config";
import { getFirstErrorMessage } from "../../twh/functions";

const GoogleMyBusinessSelector = () => {
  const [locations, setLocations] = useState([]);
  const [selectedLocation, setSelectedLocation] = useState('');
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);

  const navigate = useNavigate();     // ← added

  // Fetch locations on mount
  useEffect(() => {
    const fetchLocations = async () => {
      try {
        setLoading(true);
        setError(null);
        const response = await fetch(`${config.reactApi_baseUrl}/react-api/integrations/google-my-business-place.php`, {
          method: 'GET',
          credentials: 'include',
        });
        const data = await response.json();
        console.log(data);
        if (data.status === true && data.response?.locations) {
          setLocations(data.response.locations);
        } else {
          setError(data.errorMsg || 'Failed to load locations');
        }
      } catch (err) {
        setError('Network error: ' + err.message);
      } finally {
        setLoading(false);
      }
    };
    fetchLocations();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!selectedLocation) {
      setError('Please select a location');
      return;
    }

    setSubmitting(true);
    setError(null);
    setSuccess(false);

    try {
      const formData = new FormData();
      formData.append('issetGooglePlaceID', 'true');
      formData.append('google_my_business_location_id', selectedLocation);

      const response = await fetch(`${config.reactApi_baseUrl}/react-api/integrations/google-my-business-location-update.php`, {
        method: 'POST',
        body: formData,
        credentials: 'include',
      });

      const data = await response.json();

      if (data.status === true) {
        setSuccess(true);
        // Redirect after short delay so user can see success message (optional)
        setTimeout(() => {
          window.location.href = "/marketing/review-manager";
        }, 1200);   // 1.2 seconds delay – adjust or remove as you like
      } else {
        const errMsg = getFirstErrorMessage(data) || data.errorMsg || 'Failed to save changes';
        setError(errMsg);
      }
    } catch (err) {
      setError('Submit error: ' + err.message);
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="300px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{}}>
      {/* Alert / Notification area */}
      {error && <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 3 }}>Changes saved successfully! Redirecting...</Alert>}

      <form onSubmit={handleSubmit}>
        <Card elevation={3} sx={{ overflow: 'visible', px: 5 }}>
          <Box sx={{ textAlign: 'center', pt: 5, pb: 3, bgcolor: 'white' }}>
            <img
              src={`${config.reactApi_baseUrl}/media/images/google-review.png`}
              alt="Google My Business"
              style={{ width: 150, height: 150, objectFit: 'contain' }}
            />
          </Box>

          <CardContent sx={{ px: 4, pb: 4 }}>
            <Typography variant="h6" fontWeight="bold" gutterBottom>
              Google My Business
            </Typography>

            <FormControl component="fieldset" fullWidth>
              <RadioGroup
                aria-label="google-my-business-location"
                name="google_my_business_location_id"
                value={selectedLocation}
                onChange={(e) => setSelectedLocation(e.target.value)}
              >
                {locations.length === 0 ? (
                  <Typography color="text.secondary" align="center" py={4}>
                    No location available.
                  </Typography>
                ) : (
                  locations.map((loc) => {
                    const locationId = loc.name;
                    const title = loc.title || 'Unknown';
                    const category = loc.categories?.primaryCategory?.displayName || 'No category';

                    return (
                      <Card
                        key={locationId}
                        variant="outlined"
                        sx={{
                          mb: 2,
                          borderColor: selectedLocation === locationId ? 'primary.main' : 'grey.300',
                          bgcolor: selectedLocation === locationId ? 'action.selected' : 'white',
                          transition: 'all 0.2s',
                          '&:hover': { borderColor: 'primary.light' },
                        }}
                      >
                        <FormControlLabel
                          value={locationId}
                          control={<Radio sx={{ ml: 1 }} />}
                          label={
                            <Box sx={{ flex: 1, p: 1 }}>
                              <Typography variant="subtitle1" fontWeight="bold">
                                {title}
                              </Typography>
                              <Typography variant="body2" color="text.secondary">
                                {category}
                              </Typography>
                            </Box>
                          }
                          labelPlacement="end"
                          sx={{
                            width: '100%',
                            m: 0,
                            alignItems: 'center',
                          }}
                        />
                      </Card>
                    );
                  })
                )}
              </RadioGroup>
            </FormControl>

            <Box sx={{ textAlign: 'right', mt: 4 }}>
              <Button
                type="submit"
                variant="contained"
                disabled={submitting || !selectedLocation}
                sx={{
                  bgcolor: '#e91e63',
                  '&:hover': { bgcolor: '#c2185b' },
                  px: 4,
                  py: 1.2,
                  textTransform: 'none',
                  fontWeight: 'bold',
                }}
              >
                {submitting ? <CircularProgress size={24} color="inherit" /> : 'Save Changes'}
              </Button>
            </Box>
          </CardContent>
        </Card>
      </form>
    </Box>
  );
};

export default GoogleMyBusinessSelector;